import { cn } from "@volca/ui/lib/utils";
import { judge, points } from "@volca/ui/trades";
import { MissingValue } from "@/profitability/missing-value";
import { pct } from "./format";
import type { TrendDataset, TrendPeriod } from "./types";

/**
 * Each cost's share of the total, over the records that have both: a cost
 * not posted yet is missing, not zero, and leaving it in as zero would
 * understate the share (FIN-004). Null when no record has it.
 */
export const shares = (
  period: TrendPeriod | undefined,
  dataset: TrendDataset,
) => {
  const rows = period?.segments[dataset.id] ?? [];
  return Object.fromEntries(
    dataset.costs.map((cost) => {
      let whole = 0;
      let spent = 0;
      for (const row of rows) {
        const value = row.costs[cost.key];
        if (row.total === null || value === null || value === undefined)
          continue;
        whole += row.total;
        spent += value;
      }
      return [cost.key, whole ? spent / whole : null];
    }),
  );
};

/**
 * Where the latest period's total went (its revenue, for a job costing
 * report): each cost as a share of it, largest first, with the period before
 * marked on the same bar. Every cost the report keeps is listed; none folds
 * away.
 */
export const CostMix = ({
  periods,
  dataset,
  noun,
  total,
}: {
  periods: TrendPeriod[];
  dataset: TrendDataset;
  noun: string;
  /** What the costs are a share of, in a sentence: "revenue". */
  total: string;
}) => {
  const latest = periods.at(-1);
  const before = [...periods]
    .reverse()
    .slice(1)
    .find((period) => period.comparable);
  const now = shares(latest, dataset);
  const was = shares(before, dataset);
  const costs = [...dataset.costs].sort(
    (a, b) => (now[b.key] ?? 0) - (now[a.key] ?? 0),
  );
  const top = Math.max(
    ...costs.flatMap((cost) => [now[cost.key] ?? 0, was[cost.key] ?? 0]),
    0.01,
  );
  const width = (value: number) => `${(value / top) * 100}%`;
  return (
    <ul className="flex flex-col" aria-label={`Costs as a share of ${total}`}>
      {costs.map((cost) => {
        const share = now[cost.key];
        const prior = was[cost.key];
        const change =
          share !== null && prior !== null && prior !== undefined
            ? share - prior
            : null;
        return (
          <li
            key={cost.key}
            className="grid min-h-9 min-w-0 grid-cols-[minmax(0,8rem)_minmax(0,1fr)_auto] items-center gap-3 py-1 text-sm"
          >
            <span className="min-w-0 [overflow-wrap:anywhere] text-ink">
              {cost.label}
            </span>
            <span
              className="pointer-events-none relative h-2 rounded-full bg-neutral-subtle"
              aria-hidden
            >
              {share !== null && (
                <span
                  className="absolute inset-y-0 left-0 rounded-full bg-subtlest"
                  style={{ width: width(share) }}
                />
              )}
              {prior !== null && prior !== undefined && (
                <span
                  className="absolute -inset-y-1 w-0.5 rounded-full bg-ink"
                  style={{ left: width(prior) }}
                />
              )}
            </span>
            <span className="text-right whitespace-nowrap tabular-nums">
              {share === null ? <MissingValue /> : pct(share)}{" "}
              {change !== null && (
                <span
                  className={cn("text-xs text-subtle", {
                    "text-success":
                      judge(change, cost.better ?? "lower") === "good",
                    "text-danger":
                      judge(change, cost.better ?? "lower") === "bad",
                  })}
                >
                  {points(change)}
                </span>
              )}
            </span>
          </li>
        );
      })}
      {before && (
        <li className="pt-1 text-xs text-subtle">
          The mark on each bar is the {noun} before.
        </li>
      )}
    </ul>
  );
};
